import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/lib/auth";
import BrandBoard from "@/pages/BrandBoard";import PortalLogin from "@/pages/portal/PortalLogin";
import PortalLayout from "@/pages/portal/PortalLayout";
import { RequirePermission } from "@/components/RequirePermission";
import PortalPatients from "@/pages/portal/PortalPatients";
import PortalTimeline from "@/pages/portal/PortalTimeline";
import TimelineTrack from "@/pages/portal/TimelineTrack";
import PortalOverview from "@/pages/portal/PortalOverview";
import StaffManager from "@/pages/portal/StaffManager";
import PortalSettings from "@/pages/portal/PortalSettings";
import PatientDetail from "@/pages/portal/PatientDetail";
import PortalGrants from "@/pages/portal/PortalGrants";
import PortalAudit from "@/pages/portal/PortalAudit";
import ContentManager from "@/pages/portal/ContentManager";
import AnonymousInbox from "@/pages/portal/AnonymousInbox";
import EvaluationRequests from "@/pages/portal/EvaluationRequests";
import PortalMetrics from "@/pages/portal/PortalMetrics";
import DischargeMetrics from "@/pages/portal/DischargeMetrics";
import PaymentMetrics from "@/pages/portal/PaymentMetrics";
import AuthCallback from "@/pages/portal/AuthCallback";
import ApoioLogin from "@/pages/apoio/ApoioLogin";
import ApoioLayout from "@/pages/apoio/ApoioLayout";
import ApoioPatients from "@/pages/apoio/ApoioPatients";
import { ApoioBulletins, ApoioMedications } from "@/pages/apoio/ApoioPatientCategories";
import ApoioAlerts from "@/pages/apoio/ApoioAlerts";
import Activate from "@/pages/apoio/Activate";
import SaudeLayout from "@/pages/saude/SaudeLayout";
import SaudeHome from "@/pages/saude/SaudeHome";
import { TreatmentDetail, TreatmentList } from "@/pages/saude/Treatments";
import { ContentDetail, ContentLibrary, RecentContents, SavedContents, StaticPage } from "@/pages/saude/Contents";
import ContactHub from "@/pages/saude/ContactHub";
import AnonymousQuestion from "@/pages/saude/AnonymousQuestion";
import AnonymousLookup from "@/pages/saude/AnonymousLookup";
import EvaluationRequestPage from "@/pages/saude/EvaluationRequest";
import Emergency from "@/pages/saude/Emergency";
import SaudeSettings from "@/pages/saude/SaudeSettings";
import SaudeNotifications from "@/pages/saude/SaudeNotifications";
import Facility from "@/pages/saude/Facility";
import FacilityManager from "@/pages/portal/FacilityManager";
import Meetings from "@/pages/portal/Meetings";
import MeetingGrid from "@/pages/portal/MeetingGrid";
import MeetingGridRoom from "@/pages/portal/MeetingGridRoom";
import MeetingRoom from "@/pages/portal/MeetingRoom";
import MeetingDiscussion from "@/pages/portal/MeetingDiscussion";
import MeetingMinutes from "@/pages/portal/MeetingMinutes";

const PortalRoutes = () => {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="login" element={<PortalLogin />} />
      <Route element={<PortalLayout />}>
        <Route index element={<PortalOverview />} />
        <Route path="pacientes" element={<PortalPatients />} />
        <Route path="pacientes/:patientId" element={<PatientDetail />} />
        <Route
          path="linha-do-tempo"
          element={
            <RequirePermission permission="timeline">
              <PortalTimeline />
            </RequirePermission>
          }
        />
        <Route
          path="linha-do-tempo/:patientId"
          element={
            <RequirePermission permission="timeline">
              <PortalTimeline />
            </RequirePermission>
          }
        />
        <Route
          path="linha-do-tempo/:patientId/:track"
          element={
            <RequirePermission permission="timeline">
              <TimelineTrack />
            </RequirePermission>
          }
        />
        <Route path="reunioes" element={<Meetings />} />
        <Route path="reunioes/:meetingId" element={<MeetingRoom />} />
        <Route path="reunioes/:meetingId/ata" element={<MeetingMinutes />} />
        <Route path="reunioes/:meetingId/paciente/:patientId" element={<MeetingDiscussion />} />
        <Route path="grade-reunioes" element={<MeetingGrid />} />
        <Route path="grade-reunioes/:occurrenceId" element={<MeetingGridRoom />} />
        <Route path="vinculos" element={<PortalGrants />} />
        <Route path="conteudo" element={<ContentManager />} />
        <Route path="estrutura" element={<FacilityManager />} />
        <Route path="perguntas" element={<AnonymousInbox />} />
        <Route path="solicitacoes" element={<EvaluationRequests />} />
        <Route path="metricas" element={<PortalMetrics />} />
        <Route path="metricas/altas" element={<DischargeMetrics />} />
        <Route path="metricas/pagamentos" element={<PaymentMetrics />} />
        <Route path="auditoria" element={<PortalAudit />} />
        <Route
          path="funcionarios"
          element={
            <RequirePermission permission="staff_manage">
              <StaffManager />
            </RequirePermission>
          }
        />
        <Route path="configuracoes" element={<PortalSettings />} />
      </Route>
      <Route path="*" element={<Navigate to="/portal" replace />} />
    </Routes>
  );
};

const ApoioRoutes = () => (
  <Routes>
    <Route path="login" element={<ApoioLogin />} />
    <Route path="ativar/:token" element={<Activate />} />
    <Route element={<ApoioLayout />}>
      <Route index element={<ApoioPatients />} />
      <Route path="boletins" element={<ApoioBulletins />} />
      <Route path="medicamentos" element={<ApoioMedications />} />
      <Route path="alertas" element={<ApoioAlerts />} />
      <Route path="paciente/:patientId/boletins" element={<Navigate to="/apoio/boletins" replace />} />
      <Route path="paciente/:patientId/medicamentos" element={<Navigate to="/apoio/medicamentos" replace />} />
      <Route path="paciente/:patientId" element={<Navigate to="/apoio" replace />} />
    </Route>
    <Route path="*" element={<Navigate to="/apoio" replace />} />
  </Routes>
);

export default function App() {
  return (
    <BrowserRouter>
      <Toaster position="top-center" richColors />
      <Routes>
        <Route path="/" element={<Navigate to="/portal/login" replace />} />
        <Route path="/brand" element={<BrandBoard />} />
        <Route
          path="/portal/*"
          element={
            <AuthProvider namespace="portal">
              <PortalRoutes />
            </AuthProvider>
          }
        />
        <Route
          path="/apoio/*"
          element={
            <AuthProvider namespace="apoio">
              <ApoioRoutes />
            </AuthProvider>
          }
        />
        <Route path="/saude" element={<SaudeLayout />}>
          <Route index element={<SaudeHome />} />
          <Route path="tratamentos" element={<TreatmentList />} />
          <Route path="estrutura" element={<Facility />} />
          <Route path="tratamentos/:slug" element={<TreatmentDetail />} />
          <Route path="conteudos" element={<ContentLibrary />} />
          <Route path="conteudos/:slug" element={<ContentDetail />} />
          <Route path="fale-com-a-erimus" element={<ContactHub />} />
          <Route path="fale-com-a-erimus/pergunta" element={<AnonymousQuestion />} />
          <Route path="fale-com-a-erimus/consultar" element={<AnonymousLookup />} />
          <Route path="fale-com-a-erimus/avaliacao" element={<EvaluationRequestPage />} />
          <Route path="ajuda-agora" element={<Emergency />} />
          <Route path="salvos" element={<SavedContents />} />
          <Route path="recentes" element={<RecentContents />} />
          <Route path="pagina/:slug" element={<StaticPage />} />
          <Route path="configuracoes" element={<SaudeSettings />} />
          <Route path="notificacoes" element={<SaudeNotifications />} />
          <Route path="institucional" element={<Navigate to="/saude/pagina/sobre-a-erimus" replace />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
